import { put, call, all, takeLatest } from 'redux-saga/effects';
import * as api from 'constants/api.js';
import {
  SG_AP_BOOTSTRAP,
  AP_PENDING,
  AP_BOOTSTRAP,
  AP_ERROR,
  SG_AP_SUBMIT,
  AP_SUBMIT,
  SG_AP_GET_STATUS_OPTIONS,
  AP_STATUS_OPTIONS,
  SG_AP_CHECKIN_SUBMIT,
  AP_CHECKIN_SUBMIT,
  ALL_ERRORS,
} from 'constants/actions.js';

// helper: choose the right event (action planning vs check-in)
function pickActiveEvent(events, kind /* 'plan' | 'update' */) {
  const desiredType = kind === 'plan' ? 'action_planning' : 'check_in';
  if (!Array.isArray(events)) return null;
  return (
    events.find((e) => e.event_type === desiredType && e.status === 'active') ||
    events.find((e) => e.event_type === desiredType && e.status === 'scheduled') ||
    events.find((e) => e.event_type === desiredType) ||
    null
  );
}

function normalizeList(maybe) {
  if (!maybe) return [];
  if (Array.isArray(maybe)) return maybe;
  if (Array.isArray(maybe.results)) return maybe.results;
  if (Array.isArray(maybe.items)) return maybe.items;
  return [];
}

// ---- BOOTSTRAP (GET) ----
function normalizeActionsResponse(res) {
  if (!res) return [];
  if (Array.isArray(res?.data?.actions)) return res.data.actions;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.actions)) return res.actions;
  if (Array.isArray(res?.data?.results)) return res.data.results;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  return [];
}

function extractEventFromResponse(res) {
  if (!res) return null;
  return res?.data?.event || res?.event || null;
}

function* apBootstrapSaga(action) {
  const { token, kind = 'plan' } = action.payload || {};
  yield put({ type: AP_PENDING, meta: { phase: 'bootstrap', token, kind } });
  try {
    let events = [];
    let activeEvent = null;
    let campaignId = null;
    let requiredActionsCount = 3;
    let actionsList = [];

    let evRes = null;
    if (kind === 'update') {
      evRes = yield call(api.AP_GET_MY_EVENTS, token);
      const bundledEvent = evRes?.data?.event || null;
      const bundledActions = Array.isArray(evRes?.data?.actions) ? evRes.data.actions : [];
      events = bundledEvent ? [bundledEvent] : normalizeList(evRes?.data);
      activeEvent = bundledEvent || pickActiveEvent(events, kind);
      if (bundledActions.length > 0) {
        actionsList = bundledActions;
      }
    }

    let actRes = null;
    if (kind === 'plan') {
      actRes = yield call(api.AP_GET_MY_ACTIONS, token);
      const actionsFromAct = normalizeActionsResponse(actRes);
      if (actionsFromAct.length) {
        actionsList = actionsFromAct;
      }

      const embeddedEvent = extractEventFromResponse(actRes);
      if (!activeEvent && embeddedEvent) {
        activeEvent = embeddedEvent;
      }
      if (!events.length && embeddedEvent) {
        events = [embeddedEvent];
      }
    }

    const requiredActionsCountRaw = Number(
      activeEvent?.required_actions_count ?? actRes?.data?.required_actions_count ?? 0
    );
    if (requiredActionsCountRaw > 0) {
      requiredActionsCount = requiredActionsCountRaw;
    } else if (kind === 'plan' && actionsList.length > 0) {
      requiredActionsCount = actionsList.length;
    }

    campaignId = activeEvent?.campaign ?? null;

    yield put({
      type: AP_BOOTSTRAP,
      payload: {
        token,
        kind,
        events,
        actions: actionsList,
        activeEvent,
        campaignId,
        requiredActionsCount,
      },
    });
  } catch (e) {
    const status = e?.response?.status ?? e?.status ?? null;
    const data = e?.response?.data ?? null;
    const message =
      data?.message ??
      data?.detail ??
      (typeof data === 'string' ? data : e?.message || 'Unable to load action plan');
    const errorPayload = {
      status,
      data,
      message,
      kind,
      token,
    };
    yield put({ type: AP_ERROR, message: errorPayload, meta: { phase: 'bootstrap', token, kind } });
    yield put({ type: ALL_ERRORS, message: errorPayload });
  }
}

// ---- SUBMIT (PATCH) ----
function* apSubmitSaga(action) {
  // expects: { eventId, token, response_data }
  yield put({ type: AP_PENDING, meta: { phase: 'submit' } });
  try {
    const { eventId, token, response_data } = action.payload || {};
    // eslint-disable-next-line no-console
    console.log('[AP] Submitting check-in', { eventId, hasToken: Boolean(token) });
    const json = yield call(
      api.AP_PATCH_EVENT_RESPONSE,
      eventId,
      token,
      response_data
    );
    // eslint-disable-next-line no-console
    console.log('[AP] Submit success', json?.data);
    yield put({ type: AP_SUBMIT, payload: json.data });
  } catch (e) {
    const msg = e?.response || e?.message || e;
    // eslint-disable-next-line no-console
    console.warn('[AP] Submit failed', msg);
    yield put({ type: AP_ERROR, message: msg });
    yield put({ type: ALL_ERRORS, message: msg });
  }
}

// ---- CHECKIN SUBMIT (PATCH) ----
function* apCheckinSubmitSaga(action) {
  // expects: { eventId, actionId, token, checkinData, useDirect }
  yield put({ type: AP_PENDING, meta: { phase: 'checkin_submit' } });
  try {
    const { eventId, actionId, token, checkinData, useDirect } = action.payload || {};
    // eslint-disable-next-line no-console
    console.log('[AP] Submitting action check-in', { direct: !!useDirect || !eventId, eventId, actionId });
    const json = yield call(
      useDirect || !eventId ? api.AP_PATCH_DIRECT_CHECKIN : api.AP_PATCH_ACTION_CHECKIN,
      // dynamic call signature based on chosen endpoint
      ...(useDirect || !eventId
        ? [actionId, token, checkinData]
        : [eventId, actionId, token, checkinData])
    );
    // eslint-disable-next-line no-console
    console.log('[AP] Check-in submit success', json?.data);
    yield put({ type: AP_CHECKIN_SUBMIT, payload: json.data });
  } catch (e) {
    const msg = e?.response || e?.message || e;
    // eslint-disable-next-line no-console
    console.warn('[AP] Check-in submit failed', msg);
    yield put({ type: AP_ERROR, message: msg });
    yield put({ type: ALL_ERRORS, message: msg });
  }
}

// ---- STATUS OPTIONS (GET) ----
function* apGetStatusOptionsSaga(action) {
  const { token } = action.payload || {};
  yield put({ type: AP_PENDING, meta: { phase: 'status_options' } });
  try {
    const res = yield call(api.AP_GET_STATUS_OPTIONS, token);
    const opts = Array.isArray(res?.data?.status_options)
      ? res.data.status_options
      : normalizeList(res?.data);
    yield put({ type: AP_STATUS_OPTIONS, payload: opts });
  } catch (e) {
    const msg = e?.response || e?.message || e;
    yield put({ type: AP_ERROR, message: msg });
    yield put({ type: ALL_ERRORS, message: msg });
  }
}

export default function* ActionPlan() {
  yield all([
    takeLatest(SG_AP_BOOTSTRAP, apBootstrapSaga),
    takeLatest(SG_AP_SUBMIT, apSubmitSaga),
    takeLatest(SG_AP_CHECKIN_SUBMIT, apCheckinSubmitSaga),
    takeLatest(SG_AP_GET_STATUS_OPTIONS, apGetStatusOptionsSaga),
  ]);
}
